import { useCallback, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { PanelLeftClose } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { DebouncedSearchInput } from '@/components/ui/debounced-search-input';
import {
  isSubGroup,
  type MenuChild,
  type MenuGroup,
  type MenuLeaf,
  type MenuSubGroup,
} from '../lib/menu';
import { resolveLitHref } from '../lib/navRouteMatchers';
import { useVisibleMenu } from '../hooks/useVisibleMenu';
import { useShellStore } from '../store/shellStore';
import { QuickActions } from './QuickActions';
import { RecentSection } from './RecentSection';

/**
 * The 264px module panel (M2 · 86d44z1p4) with its search field (M5 · 86d44z24u).
 *
 * ⛔ IN-FLOW, not an overlay — it PUSHES `main`. The rail and this panel share `--sidebar` and a
 * `border-r`, so they read as one continuous surface with a hairline seam at 68px: no radius, no
 * shadow, no scrim. The old sidebar was an absolutely-positioned overlay with an in-flow spacer;
 * this is a real flex child of AppLayout's row, which is why AppLayout owns the restructure.
 *
 * Slots are filled by later tickets: quick actions (M3), recent (M4).
 */

export function ModulePanel() {
  const activeModule = useShellStore((s) => s.activeModule);
  const visibleMenu = useVisibleMenu();

  const entry = visibleMenu.find((e) => e.id === activeModule);
  // Only grouped modules have a panel. Rail leaves (Home / What's New / Downloads) navigate and
  // close it, so there is nothing to draw. A module filtered away by permissions lands here too.
  if (!entry || entry.type !== 'group') return null;

  // ⚠️ `key` is doing real work: it resets the search query for free whenever the user switches
  // module. Without it, switching MRO (query "pick") → Sales shows an EMPTY panel with a stale
  // query and no visible cause. Closing the panel unmounts the whole thing, covering the other
  // half of the lifecycle.
  return <ModulePanelBody key={entry.id} entry={entry} />;
}

function ModulePanelBody({ entry }: { entry: MenuGroup }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const collapsePanel = useShellStore((s) => s.collapsePanel);

  /* M5 — the query lives HERE, in the panel: not in a shell store, not in AppLayout. The rail and
     the page are SIBLINGS of this component, so a keystroke cannot re-render either of them; the
     only subtree that re-renders is this panel's ~12 rows. That is what makes `delay={0}` safe
     (performance gate rules 6–7). No debounce is the CORRECT choice here because there is no
     remote work — debouncing an in-memory filter of ten rows only makes typing feel laggy. */
  const [query, setQuery] = useState('');

  // Active matching goes through M1's matcher, NOT exact equality — exact equality leaves every
  // detail page (a picklist, a PO, a document) with no nav row lit.
  const litHref = resolveLitHref(pathname);

  const rows = filterChildren(entry.children, query);
  const matches = flattenLeaves(rows);
  const searching = query.trim().length > 0;

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter') {
        // Exactly one survivor means the user has already disambiguated — take them there.
        if (matches.length === 1) navigate(matches[0].href);
        return;
      }
      if (e.key === 'Escape' && query.length > 0) {
        setQuery('');
        // AppLayout listens for Escape on `window` to close notifications. Clearing a query is a
        // complete action in itself, so it must not also reach that handler.
        e.stopPropagation();
      }
    },
    [matches, navigate, query]
  );

  const Icon = entry.icon;

  return (
    <aside
      aria-label={entry.label}
      // Industrial re-bases the generic tokens for this subtree via this attribute — see the
      // [data-surface='sidebar'] block in globals.css. Never the bare `aside` selector.
      data-surface="sidebar"
      // Hand-written keyframe. `animate-in slide-in-from-left-2` compiles to NOTHING here:
      // tailwindcss-animate is not installed. It honours prefers-reduced-motion in globals.css.
      className="shell-panel-enter flex w-panel min-h-0 flex-shrink-0 flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground"
    >
      <header className="flex flex-shrink-0 items-center gap-2 px-3 pb-2 pt-3.5">
        <Icon className="h-4 w-4 flex-shrink-0 text-muted-foreground" aria-hidden="true" />
        <h2 className="truncate text-sm font-bold tracking-tight">{entry.label}</h2>
        {entry.tag && (
          <span className="flex-shrink-0 rounded border border-sidebar-border px-1 py-px text-2xs font-semibold uppercase tracking-wider text-muted-foreground">
            {entry.tag}
          </span>
        )}
        {/* Icon-only control, so it carries a Tooltip AND a matching aria-label (UI gate §5). */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={collapsePanel}
              aria-label="Collapse panel"
              className="ml-auto h-6 w-6 flex-shrink-0 text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground"
            >
              <PanelLeftClose className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">Collapse panel</TooltipContent>
        </Tooltip>
      </header>

      {/* ⛔ No ⌘K chip — the designer removed it from both fields because the app has no such
          shortcut, and advertising a shortcut that does not exist is worse than no hint.
          No autofocus either: the panel opens on every rail click, and stealing focus from the
          page each time would make the rail unusable. */}
      <div
        className="flex-shrink-0 border-b border-sidebar-border px-2.5 pb-2.5"
        onKeyDown={onKeyDown}
      >
        <DebouncedSearchInput
          value={query}
          onValueChange={setQuery}
          delay={0}
          placeholder={`Search ${entry.label}…`}
          inputClassName="h-8 text-sm"
        />
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto px-2 py-2.5">
        {/* Hidden entirely — heading included — when this user has no actions here (M3). */}
        <QuickActions moduleId={entry.id} />

        <SectionLabel className="pb-1.5">Navigate</SectionLabel>
        {rows.map((child) =>
          isSubGroup(child) ? (
            <NavSection key={child.label} child={child} litHref={litHref} />
          ) : (
            <NavRow key={child.href} leaf={child} litHref={litHref} />
          )
        )}

        {searching && matches.length === 0 && (
          // A blank 264px column is a gate violation. One muted line, sized to the column rather
          // than the page-sized shared EmptyState.
          // ⚠️ NOT `text-muted-foreground` — that token is 2.60:1 on the Industrial charcoal panel.
          <p className="px-2.5 py-3 text-sm text-sidebar-foreground/70">
            No matches in {entry.label}.
          </p>
        )}

        {/* Hidden entirely — heading included — when this module has nothing recent (M4). */}
        <RecentSection moduleId={entry.id} />
      </div>
    </aside>
  );
}

/**
 * Filter a module's children by label, case-insensitive substring.
 *
 * ⚠️ Runs over the PERMISSION-FILTERED tree it is handed (`useVisibleMenu`), never the static menu
 * definition — otherwise the box surfaces routes the user cannot open.
 *
 * ⚠️ Sections are dropped WITH their children, never orphaned. A naive `.filter()` over leaves
 * leaves "Receiving" and "Issuance" standing above nothing. A match on the SECTION label keeps all
 * of that section's children, which is what makes typing "receiving" useful rather than empty.
 */
function filterChildren(children: MenuChild[], query: string): MenuChild[] {
  const q = query.trim().toLowerCase();
  if (q.length === 0) return children;
  const hit = (text: string) => text.toLowerCase().includes(q);

  return children.flatMap((child): MenuChild[] => {
    if (!isSubGroup(child)) return hit(child.label) ? [child] : [];
    if (hit(child.label)) return [child];
    const kids = child.children.filter((leaf) => hit(leaf.label));
    return kids.length > 0 ? [{ ...child, children: kids }] : [];
  });
}

/** Every leaf across both depths, in render order — what Enter disambiguates against. */
function flattenLeaves(children: MenuChild[]): MenuLeaf[] {
  return children.flatMap((child) => (isSubGroup(child) ? child.children : [child]));
}

function SectionLabel({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <p
      className={cn(
        'm-0 px-2.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground',
        className
      )}
    >
      {children}
    </p>
  );
}

/** A depth-2 section (only MRO has any) and its indented leaves. */
function NavSection({ child, litHref }: { child: MenuSubGroup; litHref: string | null }) {
  // Must-fix 2 — a section whose every leaf was permission-filtered must not render its header.
  // `useVisibleMenu` already drops such sections; this is the belt to that pair of braces, because
  // the failure mode is an orphaned header floating above nothing.
  if (child.children.length === 0) return null;
  const Icon = child.icon;

  return (
    <>
      <div className="flex items-center gap-2 px-2.5 pb-1 pt-2.5">
        <Icon className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" aria-hidden="true" />
        <span className="truncate text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {child.label}
        </span>
      </div>
      {child.children.map((leaf) => (
        <NavRow key={leaf.href} leaf={leaf} litHref={litHref} indented />
      ))}
    </>
  );
}

function NavRow({
  leaf,
  litHref,
  indented = false,
}: {
  leaf: MenuLeaf;
  litHref: string | null;
  indented?: boolean;
}) {
  const Icon = leaf.icon;
  const active = litHref !== null && litHref === leaf.href;
  const badge = leaf.badge ?? 0;

  return (
    // `Link`, not `NavLink` — see the rail. NavLink's own prefix matcher would be a SECOND source
    // of truth for "active" that agrees with resolveLitHref only by luck, and disagrees the moment
    // a route stops being a clean prefix of its nav href.
    <Link
      to={leaf.href}
      aria-current={active ? 'page' : undefined}
      // A PANEL nav row navigates and leaves the panel open — unlike a rail leaf, which closes it.
      className={cn(
        'relative flex items-center gap-2.5 rounded-md px-2.5 py-1.5 transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring',
        indented && 'ml-3',
        active
          ? 'bg-sidebar-active font-semibold text-sidebar-active-foreground'
          : 'text-sidebar-foreground hover:bg-sidebar-accent'
      )}
    >
      {active && (
        <span
          aria-hidden="true"
          className="absolute -left-2 top-1/2 h-4 w-1 -translate-y-1/2 rounded-r-full bg-sidebar-ring"
        />
      )}
      <Icon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
      <span className="min-w-0 flex-1 truncate text-sm">{leaf.label}</span>
      {badge > 0 && (
        <span
          className="inline-flex h-4 min-w-4 flex-shrink-0 items-center justify-center rounded-full bg-capitol-red px-1 text-2xs font-semibold text-white"
          aria-label={`${badge} needing attention`}
        >
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </Link>
  );
}
